import dagre from 'dagre';
import { Edge, MarkerType, Node, Position } from '@xyflow/react';
import { SkillsNodeData, SkillsTreeNode } from './types';

export type LayoutDirection = 'TB' | 'LR';

type BuildLayoutOptions = {
  direction?: LayoutDirection;
  rankSep?: number;
  nodeSep?: number;
};

export const SKILLS_NODE_WIDTH = 230;
export const SKILLS_NODE_HEIGHT = 132;

export function buildParentMap(nodes: SkillsTreeNode[]): Map<string, string | null> {
  return new Map(nodes.map((node) => [node.id, node.parentId]));
}

export function getAncestorSet(
  selectedId: string | null,
  parentMap: Map<string, string | null>,
): Set<string> {
  const ancestors = new Set<string>();
  if (!selectedId) return ancestors;

  let current: string | null | undefined = selectedId;
  while (current) {
    ancestors.add(current);
    current = parentMap.get(current);
  }

  return ancestors;
}

export function getAncestorEdgeSet(
  selectedId: string | null,
  parentMap: Map<string, string | null>,
): Set<string> {
  const chainEdges = new Set<string>();
  if (!selectedId) return chainEdges;

  let current: string | null | undefined = selectedId;
  while (current) {
    const parent = parentMap.get(current);
    if (!parent) break;
    chainEdges.add(`${parent}=>${current}`);
    current = parent;
  }

  return chainEdges;
}

export function buildTreeLayout(
  treeNodes: SkillsTreeNode[],
  options: BuildLayoutOptions = {},
): {
  nodes: Node<SkillsNodeData, 'skillNode'>[];
  edges: Edge[];
  parentMap: Map<string, string | null>;
} {
  const direction = options.direction ?? 'TB';
  const rankSep = options.rankSep ?? 140;
  const nodeSep = options.nodeSep ?? 64;

  const dagreGraph = new dagre.graphlib.Graph({ multigraph: false, compound: false });
  dagreGraph.setDefaultEdgeLabel(() => ({}));
  dagreGraph.setGraph({
    rankdir: direction,
    ranksep: rankSep,
    nodesep: nodeSep,
    marginx: 36,
    marginy: 36,
  });

  const parentMap = buildParentMap(treeNodes);

  treeNodes.forEach((node) => {
    dagreGraph.setNode(node.id, {
      width: SKILLS_NODE_WIDTH,
      height: SKILLS_NODE_HEIGHT,
    });
    if (node.parentId) {
      dagreGraph.setEdge(node.parentId, node.id);
    }
  });

  dagre.layout(dagreGraph);

  const nodes: Node<SkillsNodeData, 'skillNode'>[] = treeNodes.map((node) => {
    const position = dagreGraph.node(node.id);
    return {
      id: node.id,
      type: 'skillNode',
      data: {
        node,
        isSelected: false,
        isAncestor: false,
        isDropTarget: false,
        isDragging: false,
        isLocked: false,
        canAddChild: false,
        canDelete: false,
      },
      position: {
        x: position.x - SKILLS_NODE_WIDTH / 2,
        y: position.y - SKILLS_NODE_HEIGHT / 2,
      },
      sourcePosition: direction === 'TB' ? Position.Bottom : Position.Right,
      targetPosition: direction === 'TB' ? Position.Top : Position.Left,
      draggable: node.parentId !== null,
      selectable: true,
    };
  });

  const edges: Edge[] = treeNodes
    .filter((node) => node.parentId)
    .map((node) => ({
      id: `edge:${node.parentId}->${node.id}`,
      source: node.parentId as string,
      target: node.id,
      type: 'smoothstep',
      animated: false,
      markerEnd: {
        type: MarkerType.ArrowClosed,
        width: 18,
        height: 18,
      },
      style: {
        stroke: 'rgba(158, 176, 222, 0.58)',
        strokeWidth: 1.7,
      },
    }));

  return { nodes, edges, parentMap };
}
